/**
 * Hooks for the platform's own achievements.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { systemAchievementsApi } from '@api/endpoints/systemAchievements'
import type {
  CreateSystemAchievementRequest,
  UpdateSystemAchievementRequest,
} from '@api/types/systemAchievement'

const KEY = ['system-achievements']

export function useSystemAchievements() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => systemAchievementsApi.getAll(),
    staleTime: 5 * 60 * 1000,
  })
}

/** The full record, which the list does not carry — the form needs the long description. */
export function useSystemAchievement(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, id],
    queryFn: () => systemAchievementsApi.getById(id!),
    enabled: !!id,
  })
}

export function useCreateSystemAchievement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateSystemAchievementRequest) => systemAchievementsApi.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  })
}

export function useUpdateSystemAchievement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSystemAchievementRequest }) =>
      systemAchievementsApi.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  })
}

export function useDeleteSystemAchievement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => systemAchievementsApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  })
}

/** Upload/replace an achievement's icon. Called after create/update so the id already exists. */
export function useUploadSystemAchievementIcon() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      systemAchievementsApi.uploadIcon(id, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  })
}

/**
 * The one-off repair for badges awarded before the award path learned to date itself.
 *
 * Not invalidating the achievement list on success: this changes completion dates on progress
 * rows, which that list does not show, and the report is the thing worth looking at afterwards.
 */
export function useRedateEarnedAchievements() {
  return useMutation({
    mutationFn: (apply: boolean) => systemAchievementsApi.redateEarned(apply),
  })
}

export function useRecalculateCriteria() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (criteriaType: number) => systemAchievementsApi.recalculate(criteriaType),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  })
}
